import React, {FC} from 'react';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ClientAtom, isFunctionAtom} from '../../../editors/expressions/expressionAtomModel';
import {TransformerIdsEnum} from '../../../editors/expressions/expressionInferHelpers';
import {ExpressionDependencies} from '../../../editors/expressions/expressionState';
import {ResultTypesEnum} from '../../../editors/expressions/infer/expressionResultModel';
import {AddAtomCard} from './addAtomCard';
import {AddPeriodAtomCard} from './addPeriodAtomCard';
import {CompareAtomCard} from './compareAtomCard';
import {DefaultAtomCard} from './defaultAtomCard';
import {ExtractAtomCard} from './extractAtomCard';
import {ExtractTextAtomCard} from './extractTextAtomCard';
import {FactoryValueAtomCard} from './factoryValueAtomCard';
import {FetchAtomCard} from './fetchAtomCard';
import {FormatDateAtomCard} from './formatDateAtomCard';
import {FunctionAtomCard} from './functionAtomCard';
import {FunctionStartAtomCard} from './functionStartAtomCard';
import {GetCustomFieldAtomCard} from './getCustomFieldAtomCard';
import {GetHeaderAtomCard} from './getHeaderAtomCard';
import {GetJsonPropertyAtomCard} from './getJsonPropertyAtomCard';
import {GetMessageBodyAtomCard} from './getMessageBodyAtomCard';
import {GetNthAtomCard} from './getNthAtomCard';
import {GetPropertyAtomCard} from './getPropertyAtomCard';
import {GetRecipientsAtomCard} from './getRecipientsAtomCard';
import {GetTableColumnCard} from './getTableColumnCard';
import {JoinAtomCard} from './joinAtomCard';
import {PreviousAtomCard} from './previousAtomCard';
import {SliceAtomCard} from './sliceAtomCard';
import {SplitAtomCard} from './splitAtomCard';
import {StartOfAtomCard} from './startOfAtomCard';
import {StringTemplateAtomCard} from './stringTemplateAtomCard';
import {SubStringAtomCard} from './subStringAtomCard';
import {TagAtomCard} from './tagAtomCard';

/*
 * Types.
 */

export interface AtomCardProps {
  atom: ClientAtom;
  expressionDependencies: ExpressionDependencies;

  isDisabled?: boolean;
  onUpdateAtom: HandlerOf<ClientAtom>;
  onAddAtom?: HandlerOf<ClientAtom>;
  onDeleteAtom?: HandlerOf<ClientAtom>;
  onCancel?: Handler;
}

/*
 * Component.
 */

export const AtomCard: FC<AtomCardProps> = (props) => {
  const {atom, isDisabled, onUpdateAtom, onAddAtom, onDeleteAtom} = props;
  const onCancel = isDisabled ? undefined : props.onCancel;

  const AtomCardComponent = getAtomCardComponent(atom);

  return (
    <AtomCardComponent
      atom={atom}
      onAddAtom={onAddAtom}
      onUpdateAtom={onUpdateAtom}
      onDeleteAtom={onDeleteAtom}
      onCancel={onCancel}
      isDisabled={isDisabled}
      expressionDependencies={props.expressionDependencies}
    />
  );
};

function getAtomCardComponent(atom: ClientAtom): FC<AtomCardProps> {
  if (isFunctionAtom(atom)) {
    return FunctionAtomCard;
  }

  switch (atom.transform) {
    case TransformerIdsEnum.GET:
      return GetPropertyAtomCard;

    case TransformerIdsEnum.COMPARE:
      return CompareAtomCard;

    case TransformerIdsEnum.NTH:
      return GetNthAtomCard;

    case TransformerIdsEnum.BEGINNING:
    case TransformerIdsEnum.END:
      return SliceAtomCard;

    case TransformerIdsEnum.GET_CUSTOM_FIELD:
      return GetCustomFieldAtomCard;

    case TransformerIdsEnum.GET_COLUMN_VALUE:
      return GetTableColumnCard;

    case TransformerIdsEnum.ADD:
    case TransformerIdsEnum.SUBTRACT:
      return atom.previousInfer.type === ResultTypesEnum.DATETIME ? AddPeriodAtomCard : AddAtomCard;

    case TransformerIdsEnum.SUBSTRING:
      return SubStringAtomCard;

    case TransformerIdsEnum.EXTRACT_EMAILS:
    case TransformerIdsEnum.EXTRACT_URLS:
      return ExtractAtomCard;

    case TransformerIdsEnum.EXTRACT_TEXT:
      return ExtractTextAtomCard;

    case TransformerIdsEnum.SPLIT:
      return SplitAtomCard;

    case TransformerIdsEnum.JOIN:
      return JoinAtomCard;

    case TransformerIdsEnum.CREATE_STRING:
    case TransformerIdsEnum.CREATE_URL:
    case TransformerIdsEnum.CREATE_TEAMMATE_LIST:
    case TransformerIdsEnum.GET_CIRCLE:
      return FactoryValueAtomCard;

    case TransformerIdsEnum.GET_RECIPIENTS:
      return GetRecipientsAtomCard;

    case TransformerIdsEnum.FUNCTION_START:
      return FunctionStartAtomCard;

    case TransformerIdsEnum.USE_PREVIOUS:
      return PreviousAtomCard;

    case TransformerIdsEnum.TO_URL:
    case TransformerIdsEnum.GET_QUERY_PARAM:
      return StringTemplateAtomCard;

    case TransformerIdsEnum.TO_TAG:
      return TagAtomCard;

    case TransformerIdsEnum.GET_MESSAGE_BODY:
      return GetMessageBodyAtomCard;

    case TransformerIdsEnum.GET_HEADER:
      return GetHeaderAtomCard;

    case TransformerIdsEnum.FORMAT:
      return FormatDateAtomCard;

    case TransformerIdsEnum.START_OF:
    case TransformerIdsEnum.END_OF:
      return StartOfAtomCard;

    case TransformerIdsEnum.FETCH:
      return FetchAtomCard;

    case TransformerIdsEnum.GET_JSON_PROPERTY:
      return GetJsonPropertyAtomCard;

    default:
      return DefaultAtomCard;
  }
}
